import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { ConversationId as ConversationIdSchema } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse as ConversationListItemResponseType } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import {
  ConversationListItemResponse,
  type UpdateConversationInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { BasicIndex } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Option } from "effect";
import { catchApiErrors } from "./error-handling";

const FIVE_MINUTES_MS = 5 * 60 * 1000;
export const DEFAULT_PAGE_SIZE = 50;

/** Number of pages to prefetch ahead of the current page */
export const PREFETCH_PAGES_AHEAD = 3;

function toOptionIfPresent<T>(
  value: T | null | undefined,
): Option.Option<T> | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === null ? Option.none() : Option.some(value);
}

function toUpdateConversationInput(
  changes: Partial<ConversationListItemResponseType>,
): UpdateConversationInput {
  const input: UpdateConversationInput = {
    status: changes.status,
    priority: toOptionIfPresent(changes.priority),
    readAt: toOptionIfPresent(changes.readAt),
    mongoAssigneeId: toOptionIfPresent(changes.mongoAssigneeId),
    mongoCustomerId: toOptionIfPresent(changes.mongoCustomerId),
    tagUpdate: changes.tagIds
      ? { _tag: "replace", tagIds: changes.tagIds }
      : undefined,
    snoozedUntil: changes.snoozedUntil,
  };
  return input;
}

export const conversationListQueryKeys = {
  list: () => ["conversations", "list"] as const,
};

export async function fetchSingleConversation(
  effectApi: EffectApiContext,
  conversationId: ConversationId,
): Promise<ConversationListItemResponseType> {
  const client = effectApi.getClient();
  return effectApi.runPromise(
    catchApiErrors(
      client.conversation.getConversation({
        path: { id: ConversationIdSchema.make(conversationId) },
      }),
    ),
  );
}

export interface CreateConversationsListCollectionOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
}

export interface PageLoadResult {
  items: ConversationListItemResponseType[];
  totalCount: number;
  page: number;
}

export function serializeConversation(
  conversation: ConversationListItemResponseType,
): typeof ConversationListItemResponse.Encoded {
  return {
    ...conversation,
    readAt: conversation.readAt?.toISOString() ?? null,
    lastCustomerMessageAt:
      conversation.lastCustomerMessageAt?.toISOString() ?? null,
    lastMerchantMessageAt:
      conversation.lastMerchantMessageAt?.toISOString() ?? null,
    createdAt: conversation.createdAt?.toISOString() ?? null,
    statusUpdatedAt: conversation.statusUpdatedAt?.toISOString() ?? null,
    snoozedUntil: conversation.snoozedUntil?.toISOString() ?? null,
  };
}

export function createConversationsListCollection({
  queryClient,
  effectApi,
}: CreateConversationsListCollectionOptions) {
  const collection = createCollection(
    queryCollectionOptions({
      queryKey: conversationListQueryKeys.list(),
      syncMode: "eager",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      // Start with empty data - pages are loaded via React Query
      queryFn: async (): Promise<ConversationListItemResponseType[]> => [],
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      onUpdate: async ({ transaction }) => {
        const client = effectApi.getClient();

        for (const mutation of transaction.mutations) {
          const payload = toUpdateConversationInput(mutation.changes);
          try {
            await effectApi.runPromise(
              catchApiErrors(
                client.conversation.updateConversation({
                  path: { id: ConversationIdSchema.make(mutation.key) },
                  payload,
                }),
              ),
            );
          } catch (err) {
            console.error(
              "[conversations] PATCH failed for",
              mutation.key,
              err,
            );
          }
        }
        // Don't refetch after update - we manually manage data via fetchPage/writeUpsert
        // and the queryFn returns [] which would clear the collection
        return { refetch: false };
      },
    }),
  );

  // Start sync immediately so collection is ready for manual operations
  collection.startSyncImmediate();

  const fetchPage = async (
    page: number,
    filterGroup: string | undefined,
  ): Promise<PageLoadResult> => {
    const offset = page * DEFAULT_PAGE_SIZE;
    const client = effectApi.getClient();

    const result = await effectApi.runPromise(
      catchApiErrors(
        client.conversation.getFilteredConversations({
          urlParams: { limit: DEFAULT_PAGE_SIZE, offset, filterGroup },
        }),
      ),
    );

    // Upsert items into TanStack DB collection for live queries.
    // Using writeUpsert (not writeInsert) because items may already exist
    // from previous page loads or real-time updates.
    collection.utils.writeBatch(() => {
      for (const item of result.items) {
        collection.utils.writeUpsert(item);
      }
    });

    return { items: [...result.items], totalCount: result.totalCount, page };
  };

  return { collection, fetchPage };
}

export type ConversationsListCollectionWithLoader = ReturnType<
  typeof createConversationsListCollection
>;
